<script setup lang="ts">
import { computed } from 'vue';
import BIcon from './BIcon.vue';

type PropsType = {
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    disabled?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    min: 0,
    max: 100,
    step: 1,
    disabled: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: number];
}>();

const isMinusDisabled = computed(() => {
    return props.disabled || props.modelValue <= props.min;
});

const isPlusDisabled = computed(() => {
    return props.disabled || props.modelValue >= props.max;
});

const decrement = () => {
    if (isMinusDisabled.value) {
        return;
    }
    const newValue = Math.max(props.min, props.modelValue - props.step);
    emit('update:modelValue', newValue);
};

const increment = () => {
    if (isPlusDisabled.value) {
        return;
    }
    const newValue = Math.min(props.max, props.modelValue + props.step);
    emit('update:modelValue', newValue);
};

const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (!isNaN(value)) {
        const clampedValue = Math.min(props.max, Math.max(props.min, value));
        emit('update:modelValue', clampedValue);
    }
};

const handleBlur = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (isNaN(value) || target.value === '') {
        emit('update:modelValue', props.min);
    }
};
</script>

<template>
    <div class="b-number-input">
        <div
            v-if="label"
            class="b-number-input__label text-body-1 font-weight-medium mb-3"
        >
            {{ label }}
        </div>
        <div class="b-number-input__controls">
            <button
                type="button"
                class="b-number-input__btn b-number-input__btn--minus"
                :disabled="isMinusDisabled"
                @click="decrement"
            >
                <BIcon
                    icon="mdi-minus"
                    size="20"
                />
            </button>
            <input
                type="number"
                class="b-number-input__value"
                :value="modelValue"
                :min="min"
                :max="max"
                :disabled="disabled"
                @input="handleInput"
                @blur="handleBlur"
            />
            <button
                type="button"
                class="b-number-input__btn b-number-input__btn--plus"
                :disabled="isPlusDisabled"
                @click="increment"
            >
                <BIcon
                    icon="mdi-plus"
                    size="20"
                />
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.b-number-input {
    &__label {
        color: rgba(var(--v-theme-on-surface), 0.87);
    }

    &__controls {
        display: flex;
        align-items: stretch;
        border: 1px solid rgb(var(--v-theme-outline));
        border-radius: 8px;
        overflow: hidden;
        background: rgb(var(--v-theme-surface));
    }

    &__btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        min-height: 48px;
        background: rgb(var(--v-theme-grey-100));
        border: none;
        cursor: pointer;
        color: rgba(var(--v-theme-on-surface), 0.6);
        transition:
            background-color 0.15s ease,
            color 0.15s ease;

        &:hover:not(:disabled) {
            background: rgb(var(--v-theme-grey-200));
            color: rgba(var(--v-theme-on-surface), 0.87);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.4;
        }

        &--minus {
            border-right: 1px solid rgb(var(--v-theme-outline));
        }

        &--plus {
            border-left: 1px solid rgb(var(--v-theme-outline));
        }
    }

    &__value {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 1.125rem;
        font-weight: 500;
        color: rgba(var(--v-theme-on-surface), 0.87);
        min-height: 48px;
        border: none;
        background: transparent;
        outline: none;
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}
</style>
